import { Button, Card } from 'reactstrap';
import { useCertificateId, useSelectedAsset } from '../../../lib/util';
import { useBrowserLocationState } from '../../../lib/util/browser';
import {
  GET_CERTIFICATE_DETAILS,
  UPDATE_CERTIFICATE,
} from '../../../graph-queries/tar/certificate';
import { useMutation, useQuery } from '@apollo/client';
import { TCertificate, TJoint } from '../../../lib/types';
import classes from './CertificateDetailsTablet.module.scss';
import { ReactTable } from '@bphxd/ds-core-react';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import {
  CertificateStatusEnum,
  Joint,
  JointInstallationStep,
} from '../../../graph-queries/__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../templates/AppLayout';
import { MAINTANENCE_PATH } from '../../../constants';
import { extractAndFormatName } from '../../organisms/AspectsTable/util';
import { formatDate } from 'date-fns';
import FormModal from '../../molecules/FormModal/FormModal';
import CertificateAttachments from '../../organisms/CertificateAttachments/CertificateAttachments';
import {
  Attach24,
  Check24,
  Download16,
  DownloadExcel24,
  Edit24,
  Lock24,
} from '@bphxd/ds-core-react/lib/icons';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import { GET_MO_DETAILS } from '../../../graph-queries/tar/job';
import ConfirmationModal from '../../molecules/ConfirmationModal/ConfirmationModal';
import { useHasMaintenanceAdminAccess } from '../../../lib/util/auth';
import JointFormTagId from '../../organisms/JointForm/JointFormTagId';
import { UPDATE_JOINT } from '../../../graph-queries/tar/joint';
import { ActionColumn } from '../CertificateMonitoring/CertificateMonitoring.util';
import { useCertificateForExport } from '../Certificates/Certificates.util';
import BulkActionsBar, { StepInfo } from './BulkActionsBar';
import BulkSigningModal from './BulkSigningModal';
import { jointStatus } from '../JointDetails/JointDetails';

export const getHighestStepNoSignedStep = (
  steps: JointInstallationStep[]
): JointInstallationStep | null => {
  const signedSteps = steps.filter((step) => {
    const signer = step.signedBy;
    return signer && signer.trim().length > 0;
  });

  if (signedSteps.length === 0) return null;

  return signedSteps.reduce((maxStep, currentStep) =>
    currentStep.stepNo > maxStep.stepNo ? currentStep : maxStep
  );
};

const CertificateDetailsTabletPage = () => {
  const navigate = useNavigate();
  const asset = useSelectedAsset();
  const hasMaintAdminAccess = useHasMaintenanceAdminAccess();
  const certificateId = useCertificateId();
  const { data: config } = useQuery(GET_CONFIG);
  const [alertText, setAlertText] = useState('');
  const exportCertificate = useCertificateForExport();

  // Bulk selection state
  const [selectedJoints, setSelectedJoints] = useState<Set<TJoint>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<StepInfo | null>(null);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Persist pagination state in browser location state
  const [pagination, setPaginationState] = useBrowserLocationState<PaginationState>(
    'certificateDetailsTablet_pagination',
    { pageIndex: 0, pageSize: 10 }
  );

  // Wrapper to handle both updater functions and direct values from Tanstack Table
  const setPagination = (
    updaterOrValue: PaginationState | ((old: PaginationState) => PaginationState)
  ) => {
    if (typeof updaterOrValue === 'function') {
      setPaginationState(updaterOrValue(pagination));
    } else {
      setPaginationState(updaterOrValue);
    }
  };

  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;

  const isBulkJointSigningFeatureFlagEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_ENABLE_BULK_JOINT_SIGNING'
  )?.isEnabled;

  const { data, loading } = useQuery(GET_CERTIFICATE_DETAILS, {
    variables: { certificateId },
    fetchPolicy: 'network-only',
  });
  const certificateDetails = data?.certificateDetails as unknown as TCertificate;

  const { data: moData, loading: moLoading } = useQuery(GET_MO_DETAILS, {
    variables: {
      moNumber: data?.certificateDetails?.maintenanceOrder || '',
      isRealTime: isRealTimeEnabled,
    },
    skip: !data?.certificateDetails?.maintenanceOrder,
  });

  const [updateCertificate] = useMutation(UPDATE_CERTIFICATE, {
    refetchQueries: [
      {
        query: GET_CERTIFICATE_DETAILS,
        variables: { certificateId },
      },
    ],
  });

  const handleStatusChange = (unlock: boolean) => {
    updateCertificate({
      variables: {
        input: {
          certificateId,
          assetId: certificateDetails?.assetId,
          title: certificateDetails?.title,
          type: certificateDetails?.type,
          certificateNumber: certificateDetails?.certificateNumber,
          status: unlock ? CertificateStatusEnum.Draft : CertificateStatusEnum.Complete,
          template: certificateDetails?.template,
        },
      },
    }).then(() => setAlertText('Certificate status updated'));
  };

  const [updateJoint, { loading: isUpdating }] = useMutation(UPDATE_JOINT, {
    refetchQueries: [
      {
        query: GET_CERTIFICATE_DETAILS,
        variables: { certificateId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const onEditSubmit = (joint: TJoint) => {
    const { updatedAt, ...input } = joint;
    return updateJoint({ variables: { input } }).then(() =>
      setAlertText('Joint successfully updated')
    );
  };

  useEffect(() => {
    if (alertText) {
      const timer = setTimeout(() => setAlertText(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [alertText]);

  const handleExportExcel = () => exportCertificate(certificateId);

  const handleClearSelection = () => {
    setSelectedJoints(new Set());
    // clear row selection in the table
    setRowSelection({});
  };

  const handleExecuteStep = (stepInfo: StepInfo) => {
    setSelectedStep(stepInfo);
    setIsBulkModalOpen(true);
  };

  const handleBulkSignComplete = () => {
    setIsBulkModalOpen(false);
    setSelectedJoints(new Set());
    setSelectedStep(null);
    setRowSelection({});
    setAlertText('Bulk signing completed successfully');
  };

  const extractSelectedJoints = () => {
    const selectedJoints = new Set<TJoint>();
    for (const key in rowSelection) {
      if (rowSelection[key]) {
        const joint = tableData[Number(key)];
        if (joint) {
          selectedJoints.add(joint);
        }
      }
    }
    return selectedJoints;
  };

  const allJointsReadyForLeakTest = (): boolean => {
    return jointStatus(certificateDetails).readyForLeakTest;
  }

  // Enable bulk joint signing only if respective feature flag is enabled and all joints are ready for atleast leak test
  const isBulkJointSigningEnabled = isBulkJointSigningFeatureFlagEnabled && allJointsReadyForLeakTest();

  useEffect(() => {
    const selectedJoints = extractSelectedJoints();
    setSelectedJoints(selectedJoints);
  }, [rowSelection]);

  const columns: ColumnDef<TJoint>[] = [
    {
      header: 'Joint Number',
      accessorKey: 'jointNumber',
      enableSorting: false,
    },
    {
      header: 'TAG ID',
      accessorKey: 'tagId',
      enableSorting: false,
      cell: (props) => {
        const item = props.row.original;
        return (
          <div>
            {item.tagId}
            <FormModal
              title="Edit Tag ID"
              renderForm={(onClose) => (
                <JointFormTagId
                  defaultValues={item}
                  onClose={onClose}
                  type="edit"
                  onSubmit={onEditSubmit}
                />
              )}
              renderButton={(onClick) => (
                <Edit24 cursor={'pointer'} onClick={onClick} title="Edit Tag ID" />
              )}
            />
          </div>
        );
      },
    },
    {
      header: 'P&ID No',
      accessorKey: 'pIdNo',
      enableSorting: false,
    },
    {
      header: 'Additional details (Iso No / Joint Ref)',
      accessorKey: 'additionalDetails',
      enableSorting: false,
    },
    {
      header: 'Last Signed Step',
      enableSorting: false,
      accessorFn: (joint: TJoint) => {
        const steps = (joint.tarJointInstallationSteps as unknown as JointInstallationStep[]) || [];
        const lastStep = getHighestStepNoSignedStep(steps);
        return lastStep
          ? `"${lastStep.stepTitle}" signed by ${extractAndFormatName(lastStep.signedBy || '')} on ${formatDate(lastStep.signedAt, 'dd/MMM/yyyy')}`
          : '';
      },
    },
    {
      header: 'Review',
      enableSorting: false,
      cell: (row) => {
        return (
          <div className={classes.review}>
            <Button
              color="primary"
              size="sm"
              disabled={
                certificateDetails.status === CertificateStatusEnum.Draft ||
                certificateDetails.status === CertificateStatusEnum.IssuedForReview ||
                certificateDetails.template === true
              }
              title={
                certificateDetails.template === true
                  ? 'This is a template. Duplicate before signing.'
                  : certificateDetails.status === CertificateStatusEnum.Draft ||
                    certificateDetails.status === CertificateStatusEnum.IssuedForReview
                    ? 'Certificate is not approved'
                    : 'Go to joint'
              }
              onClick={() => {
                navigate(
                  `/${MAINTANENCE_PATH}/joints/${row.row.original.jointId}?asset=${asset?.assetName}`
                );
              }}
            >
              Execute
            </Button>
          </div>
        );
      },
    },
  ];
  const tableData = useMemo<TJoint[]>(
    () => (data?.certificateDetails.tarJoints || []).filter((j): j is TJoint => !!j),
    [data]
  );

  return (
    <AppLayout>
      {loading ? (
        'Loading...'
      ) : (
        <div className={classes.root}>
          <section>
            <Card className={classes.details}>
              <div className={classes.title}>
                <div>Certificate Title</div>
                <div>{certificateDetails?.title}</div>
              </div>
              {certificateDetails.status !== 'COMPLETE' && (
                <div className={classes.icon}>
                  <div>Unlock</div>
                  <ConfirmationModal
                    title="Reset Status to Draft"
                    message={'Are you sure you want to change status to Draft?'}
                    confirmButtonText="Yes, change status"
                    cancelButtonText="Cancel"
                    onConfirm={() => handleStatusChange(true)}
                    renderButton={(openModal) => (
                      <Lock24
                        style={{
                          cursor: !hasMaintAdminAccess ? 'not-allowed' : 'pointer',
                          opacity: !hasMaintAdminAccess ? 0.5 : 1,
                        }}
                        onClick={!hasMaintAdminAccess ? undefined : openModal}
                        title={
                          !hasMaintAdminAccess
                            ? "You don't have sufficient privileges"
                            : 'Reset status to Draft'
                        }
                      />
                    )}
                  />
                </div>
              )}
              {certificateDetails.status !== 'COMPLETE' && (
                <div className={classes.icon}>
                  <div>Complete</div>
                  <ActionColumn certificate={certificateDetails} />
                </div>
              )}
              <div className={classes.icon}>
                <div>Attachments</div>
                <FormModal
                  title="Certificate Attachments"
                  renderForm={(onClose) => (
                    <CertificateAttachments certificateId={certificateId} onClose={onClose} />
                  )}
                  renderButton={(onClick) => (
                    <Attach24
                      onClick={onClick}
                      title="View/Add Attachments"
                      style={{ cursor: 'pointer' }}
                    ></Attach24>
                  )}
                />
              </div>
              <div className={classes.icon}>
                <div>Export</div>
                <DownloadExcel24
                  onClick={handleExportExcel}
                  title="Export Excel"
                  style={{ cursor: 'pointer' }}
                >
                  Export Excel
                </DownloadExcel24>
              </div>
              <div className={classes.createdBy}>
                <div>Created by</div>
                <div>{extractAndFormatName(certificateDetails?.createdBy)}</div>
              </div>
              <div className={classes.checkedBy}>
                <div>Checked by</div>
                <div>{extractAndFormatName(certificateDetails?.checkedBy)}</div>
              </div>

              <div>
                <div>Certificate Number</div>
                <div>
                  {certificateDetails?.certificateNumber || certificateDetails?.certificateId}
                </div>
              </div>
              <div>
                <div>Maintenance Order</div>
                <div className={classes.value}>
                  <a href={moData?.sapMo.viewInFiori || ''} target="_blank" rel="noreferrer">
                    {certificateDetails?.maintenanceOrder}
                  </a>
                </div>
              </div>
              <div>
                <div>Location</div>
                <div>{certificateDetails?.location}</div>
              </div>
              <div>
                <div>System</div>
                <div>{certificateDetails?.system}</div>
              </div>
              <div>
                <div>Permit Number</div>
                <div className={classes.value}>
                  <a
                    href={
                      `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${certificateDetails?.permitNumber?.replace('#', '%23')}?openerRegistrationNumber=Permit` ||
                      ''
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {certificateDetails?.permitNumber}
                  </a>
                </div>
              </div>
              <div>
                <div>Isolation Number</div>
                <div className={classes.value}>
                  <a
                    href={
                      `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${certificateDetails?.isolationNumber?.replace('#', '%23')}?openerRegistrationNumber=Isolation` ||
                      ''
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {certificateDetails?.isolationNumber}
                  </a>
                </div>
              </div>
            </Card>
          </section>

          {/* Bulk Actions Bar */}
          {isBulkJointSigningEnabled && (
            <BulkActionsBar
              selectedCount={selectedJoints.size}
              onClearSelection={handleClearSelection}
              onExecuteStep={handleExecuteStep}
            />
          )}

          <section className={classes.joints}>
            <ReactTable
              className={classes.jointsTable}
              columns={columns}
              data={tableData}
              pagination={pagination}
              onPaginationChange={setPagination}
              rowSelection={isBulkJointSigningEnabled ? rowSelection : undefined}
              setRowSelection={isBulkJointSigningEnabled ? setRowSelection : undefined}
            />
          </section>
        </div>
      )}

      {/* Bulk Signing Modal */}
      {isBulkModalOpen && selectedStep && (
        <BulkSigningModal
          isOpen={isBulkModalOpen}
          onClose={() => {
            setIsBulkModalOpen(false);
            setSelectedStep(null);
          }}
          selectedJoints={Array.from(selectedJoints)}
          stepNo={selectedStep.stepNo}
          stepTitle={selectedStep.stepTitle}
          certificateId={certificateId}
          onComplete={handleBulkSignComplete}
        />
      )}
    </AppLayout>
  );
};

export default CertificateDetailsTabletPage;
